import { CHAMBER_HEIGHT, type EnemyMotion, type EraSpec } from "./constants";
import { choice, hashChoice } from "./random";

export const ERA_TRANSITION_DISTANCE = 80;
export const ERA_LENGTH_CHAMBERS = [6, 9] as const;
export const ENEMY_MOTIONS: readonly EnemyMotion[] = ["static", "horizontal", "vertical", "burst", "adaptive"];

export const ERAS: readonly EraSpec[] = [
  {
    id: "cretaceous", year: "66,000,000 BCE", name: "CRETACEOUS CULTIVAR",
    paper: "#dce1b5", ink: "#294c3a", accent: "#d96b2b",
    produceWeight: 0.82, formationChance: 0.08, enemyMotionWeights: [0.72, 0.18, 0.1, 0, 0],
    movingPlatformChance: 0.06, platformTravel: [20, 32], platformPeriod: 300, spacingJitter: 12, motionPace: 0.85,
  },
  {
    id: "sun-gardens", year: "1487 CE", name: "SUN-GARDEN EMPIRE",
    paper: "#ead39b", ink: "#51392f", accent: "#b83a27",
    produceWeight: 0.72, formationChance: 0.42, enemyMotionWeights: [0.25, 0.42, 0.26, 0.07, 0],
    movingPlatformChance: 0.34, platformTravel: [26, 44], platformPeriod: 270, spacingJitter: 5, motionPace: 1,
  },
  {
    id: "steam-cannery", year: "1897 CE", name: "STEAM CANNERY AGE",
    paper: "#d8c7a6", ink: "#344b50", accent: "#a8442d",
    produceWeight: 0.22, formationChance: 0.25, enemyMotionWeights: [0.12, 0.55, 0.18, 0.15, 0],
    movingPlatformChance: 0.58, platformTravel: [38, 60], platformPeriod: 220, spacingJitter: 4, motionPace: 1.05,
  },
  {
    id: "atomic-ketchup", year: "1957 CE", name: "ATOMIC KETCHUP BOOM",
    paper: "#d8dfc7", ink: "#253e4b", accent: "#d04a2f",
    produceWeight: 0.5, formationChance: 0.36, enemyMotionWeights: [0.08, 0.3, 0.18, 0.38, 0.06],
    movingPlatformChance: 0.62, platformTravel: [42, 66], platformPeriod: 190, spacingJitter: 6, motionPace: 1.18,
  },
  {
    id: "gene-patent", year: "2088 CE", name: "GENE-PATENT FUTURE",
    paper: "#c9d8d4", ink: "#233b49", accent: "#c9342b",
    produceWeight: 0.5, formationChance: 0.3, enemyMotionWeights: [0.06, 0.22, 0.3, 0.16, 0.26],
    movingPlatformChance: 0.68, platformTravel: [36, 58], platformPeriod: 210, spacingJitter: 3, motionPace: 1.12,
  },
  {
    id: "singularity", year: "∞ + 1 CE", name: "SAUCE SINGULARITY",
    paper: "#d9cedf", ink: "#392f50", accent: "#d04438",
    produceWeight: 0.5, formationChance: 0.4, enemyMotionWeights: [0.12, 0.24, 0.22, 0.22, 0.2],
    movingPlatformChance: 0.72, platformTravel: [42, 68], platformPeriod: 190, spacingJitter: 8, motionPace: 1.18,
  },
];

export interface EraSpan {
  era: EraSpec;
  nextEra: EraSpec;
  eraNumber: number;
  startChamber: number;
  endChamber: number;
}

function eraLength(seed: number, eraNumber: number): number {
  const [minimum, maximum] = ERA_LENGTH_CHAMBERS;
  return minimum + choice(seed, eraNumber, 910, maximum - minimum + 1);
}

function eraIndex(seed: number, eraNumber: number): number {
  const cycle = Math.floor(eraNumber / ERAS.length);
  const targetPosition = eraNumber % ERAS.length;
  let used = 0;
  let selected = 0;
  for (let position = 0; position <= targetPosition; position += 1) {
    let bestIndex = -1;
    let bestRank = Number.POSITIVE_INFINITY;
    for (let index = 0; index < ERAS.length; index += 1) {
      if ((used & (1 << index)) !== 0) continue;
      const rank = hashChoice(seed, cycle * ERAS.length + index, 911);
      if (rank < bestRank) {
        bestRank = rank;
        bestIndex = index;
      }
    }
    selected = bestIndex;
    used |= 1 << selected;
  }
  return selected;
}

export function eraSpanForChamber(seed: number, chamberIndex: number): EraSpan {
  const target = Math.max(0, chamberIndex);
  let eraNumber = 0;
  let startChamber = 0;
  let length = eraLength(seed, eraNumber);
  while (target >= startChamber + length) {
    startChamber += length;
    eraNumber += 1;
    length = eraLength(seed, eraNumber);
  }
  const currentEraIndex = eraIndex(seed, eraNumber);
  const nextEraIndex = eraIndex(seed, eraNumber + 1);
  return {
    era: ERAS[currentEraIndex]!,
    nextEra: ERAS[nextEraIndex]!,
    eraNumber,
    startChamber,
    endChamber: startChamber + length,
  };
}

export function eraForChamber(seed: number, chamberIndex: number): EraSpec {
  return eraSpanForChamber(seed, chamberIndex).era;
}

export function eraTransitionAt(seed: number, worldY: number): EraSpan & { progress: number } {
  const y = Math.max(0, worldY);
  const span = eraSpanForChamber(seed, Math.floor(y / CHAMBER_HEIGHT));
  const transitionStart = span.endChamber * CHAMBER_HEIGHT - ERA_TRANSITION_DISTANCE;
  return { ...span, progress: Math.max(0, Math.min(1, (y - transitionStart) / ERA_TRANSITION_DISTANCE)) };
}
